import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {sanitize} from '@github-ui/sanitize'
import {updateLabelMutation} from '@github-ui/server-persisted-queries'
import type {UpdateLabelMutationMutation} from '@github-ui/server-persisted-queries/types/updateLabelMutation'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsQueryData} from './types'
import {formatFormErrorMessage} from './utils/format-form-error-message'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseEditLabelOptions = {
  labelId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setEditSubmissionErrors: (v: string) => void
  setIsEditDialogOpen: (v: boolean) => void
}

type EditLabelInput = {
  name: string
  description: string
  color: string
}

type MutationContext = {
  previousData: LabelsQueryData | undefined
}

export function useEditLabel({
  labelId,
  repositoryNameWithOwner,
  queryKey,
  setEditSubmissionErrors,
  setIsEditDialogOpen,
}: UseEditLabelOptions) {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useMutation<UpdateLabelMutationMutation, Error, EditLabelInput, MutationContext>({
    mutationFn: async (input: EditLabelInput) => {
      let response: Response
      try {
        response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/${labelId}`, {
          method: 'PATCH',
          body: {
            queryId: updateLabelMutation,
            name: input.name,
            description: input.description,
            color: input.color,
          },
        })
      } catch {
        throw new Error(LABELS.editLabelError)
      }

      if (!response.ok) {
        throw new Error(LABELS.editLabelError)
      }

      const result: {data: UpdateLabelMutationMutation; errors?: Array<{message: string}>} = await response.json()
      const errors = result.errors

      if (errors && errors.length > 0) {
        throw new Error(formatFormErrorMessage(errors))
      }

      if (!result.data.updateLabel?.label) {
        throw new Error(LABELS.editLabelError)
      }

      return result.data
    },
    onMutate: async (input): Promise<MutationContext> => {
      setEditSubmissionErrors('')

      const previousData = queryKey ? queryClient.getQueryData<LabelsQueryData>(queryKey) : undefined

      // Optimistically update the label in the TanStack cache
      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            const edge = labels.edges.find(e => e?.node?.id === labelId)
            if (edge?.node) {
              edge.node.name = input.name
              edge.node.nameHTML = sanitize(input.name, {
                ALLOWED_TAGS: [],
                ALLOWED_ATTR: [],
                ALLOW_ARIA_ATTR: false,
                RETURN_TRUSTED_TYPE: false,
              })
              edge.node.description = input.description
              edge.node.color = input.color
            }
          }),
        )
      }

      // Optimistically close the dialog
      setIsEditDialogOpen(false)

      return {previousData}
    },
    onError: (error, _variables, context) => {
      if (queryKey && context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      setEditSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setIsEditDialogOpen(true)
    },
    onSuccess: data => {
      const updatedLabel = data.updateLabel?.label
      if (queryKey && updatedLabel) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            const edge = labels.edges.find(e => e?.node?.id === labelId)
            if (edge?.node) {
              edge.node.name = updatedLabel.name
              edge.node.nameHTML = updatedLabel.nameHTML
              edge.node.description = updatedLabel.description
              edge.node.color = updatedLabel.color
            }
          }),
        )
      }
    },
  })

  return useCallback(
    (input: EditLabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      mutate(input)
    },
    [mutate],
  )
}
